import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QuestionnaireResultsFilter } from '@stores/QuestionnaireAdminStore';
import {
  useQuestionnaireCohorts,
  useQuestionnaireResults,
  useQuestionnaireSummaries,
  useQuestionnaireTimepoints,
} from '@app/view-creator/Admin/QuestionnaireAdminViewCreator';
import {
  filterFromSearchParams,
  hasAnyFilter,
  searchParamsFromFilter,
} from '@app/components/Admin/Questionnaires/questionnaireResultsFormatting';

/**
 * Runs the results page. The filter lives in the URL. Nothing loads until at least one filter is chosen, since every
 * sitting across every cohort is too much to show at once.
 */
export const useQuestionnaireResultsController = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => filterFromSearchParams(searchParams), [searchParams]);
  const filtered = hasAnyFilter(filter);

  const cohortsQuery = useQuestionnaireCohorts();
  const formsQuery = useQuestionnaireSummaries();
  const timepointsQuery = useQuestionnaireTimepoints();
  const resultsQuery = useQuestionnaireResults(filter, filtered);
  const [viewingSittingId, setViewingSittingId] = useState<number | null>(null);

  // Takes the selected option's id as the select reports it; an empty value clears that filter.
  const handleFilterChange = (key: keyof QuestionnaireResultsFilter, value: string) =>
    setSearchParams(searchParamsFromFilter({ ...filter, [key]: value === '' ? undefined : Number(value) }), {
      replace: true,
    });

  return {
    filter,
    filtered,
    optionsLoading: cohortsQuery.isLoading || formsQuery.isLoading || timepointsQuery.isLoading,
    optionsFailed: cohortsQuery.isError || formsQuery.isError || timepointsQuery.isError,
    cohorts: cohortsQuery.data ?? [],
    forms: formsQuery.data ?? [],
    timepoints: timepointsQuery.data ?? [],
    results: resultsQuery.data ?? [],
    resultsLoading: filtered && resultsQuery.isLoading,
    resultsFailed: resultsQuery.isError,
    viewingSittingId,
    handleBack: () => navigate('/admin/questionnaires'),
    handleFilterChange,
    handleClearFilters: () => setSearchParams({}, { replace: true }),
    handleViewResult: (sittingId: number) => setViewingSittingId(sittingId),
    handleCloseResult: () => setViewingSittingId(null),
  };
};
